import { useContext, useEffect } from 'react';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { Button } from 'components/buttons/button';
import { ButtonSize, ButtonType } from 'components/buttons/button-types';
import { FeaturePanel } from 'components/panels/feature-panel/feature-panel';
import { GetCountryCodes } from 'core/config/country-code';
import { AppContext } from 'core/context/app.context';
import { FeatureContext } from 'core/context/feature.context';
import { FeatureFlag } from 'core/enums/feature-flag';
import { Feature } from 'core/enums/features';
import { GenexActions } from 'core/enums/genex';
import { FeatureStore } from 'core/stores/feature.store';
import { GenexStore } from 'core/stores/genex.store';
import { PaymentPlatform } from 'core/stores/payment/payment.store';
import { SiteConfigStore } from 'core/stores/site-config.store';
import { DevToolsStore } from './dev-tools.store';
import { TestUsers } from './test-users';
import styles from './dev-tools.module.scss';

const DevTools = observer(() => {
  const appContext = useContext(AppContext);
  const featureContext = useContext(FeatureContext);
  const devToolsStore = useLocalObservable(() => new DevToolsStore(appContext.countryCode));

  const customerTesting = TestUsers;

  useEffect(() => {
    devToolsStore.initialize(
      FeatureStore.getFeaturePermissions(Feature.Utils, featureContext.permissionMap),
      `${appContext.accountNumber}`
    );
  }, [appContext, featureContext, devToolsStore]);

  useEffect(() => {
    devToolsStore.changeCountries(GetCountryCodes(SiteConfigStore.platform()));
  }, [SiteConfigStore.platform()]);

  const handleChangeCustomerNumber = (value: string) => {
    appContext.authStore.changeUserDetails({ customerOverride: value });
  };

  const handleChangeUser = (value: string) => {
    const user = customerTesting.find((cust) => cust.custNumber === value);

    if (user) {
      appContext.authStore.changeUserDetails({ customerOverride: user.custNumber, countryOverride: user.country });
      return;
    }

    appContext.authStore.changeUserDetails({ customerOverride: undefined, countryOverride: undefined });
  };

  const handleChangeCountry = (value: any) => {
    if (!devToolsStore.countries.some((country) => country.alpha2.toLowerCase() === value.toLowerCase())) {
      return;
    }

    appContext.authStore.changeUserDetails({ countryOverride: value });
  };

  const handleMockDataChange = (value: any) => {
    if (value) {
      localStorage.setItem('ci.mock-data-override', value);
    } else {
      localStorage.removeItem('ci.mock-data-override');
    }
  };

  const handleLogout = () => {
    appContext.authStore.logout();
  };

  const handleLogin = () => {
    appContext.authStore.login();
  };

  const handleHide = () => {
    devToolsStore.changeIsOpen(false);
  };

  const handleCSAT = () => {
    const surveyModel = {
      category: GenexActions.ClearFixDecoderErrors,
      outcome: 'Success',
    };
    GenexStore.triggerSurvey(surveyModel, appContext.customerNumber, new Date());
  };

  const handleChangePaymentPlatform = (platform: string) => {
    if (platform === '') {
      devToolsStore.changePayMethod('');
      localStorage.removeItem('ci.payment-platform-override');
      return;
    }
    localStorage.setItem('ci.payment-platform-override', platform);
    devToolsStore.changePayMethod(platform);
    appContext.changePaymentPlatform(platform as PaymentPlatform);
  };

  const clearSessionStorage = () => sessionStorage.clear();

  const refreshToken = async () => {
    await appContext.authStore.refreshToken();
  }

  return (
    <FeaturePanel hasFeature={devToolsStore.isOpen && featureContext.hasViewPermission(FeatureFlag.DevTools)}>
      <div className={`${styles.devToolDropdown} ${styles.listItemLink}`}>
        <h6>Dev Tools:</h6>
        <div className={`${styles.devToolSelect}`}>
          Lang
          <select
            name="locale"
            value={appContext.lang.toLowerCase()}
            onChange={(option) =>
              appContext.changeLocale(`${option.target?.value}-${appContext.countryCode.toLowerCase()}`)
            }
          >
            <option value="en">English</option>
            <option value="pt">Portuguese</option>
          </select>
        </div>
        <div className={`${styles.devToolSelect}`}>
          Region
          <select
            name="country"
            defaultValue={appContext.country}
            onChange={(event) => handleChangeCountry(event.target.value)}
          >
            {/* TODO: make country selection more robust

            {devToolsStore.countries.map((country) => (
              <option value={country.alpha2}>{country.full}</option>
            ))} */}
            <option value="ao">Angola</option>
            <option value="mz">Mozambique</option>
            <option value="et">Ethiopia</option>
            <option value="ng">Nigeria</option>
            <option value="za">South Africa</option>
            <option value="mu">Direct</option>
            <option value="rw">Rwanda</option>
          </select>
        </div>
        <div className={`${styles.devToolSelect}`}>
          Country Code
          <input
            name="country-code"
            disabled={false}
            defaultValue={appContext.countryCode}
            onChange={(event) => handleChangeCountry(event.target.value)}
          />
        </div>
        <div className={`${styles.devToolSelect}`}>
          Users
          <select
            name="user"
            value={appContext.customerNumber}
            onChange={(event) => handleChangeUser(event.target.value)}
          >
            <option value={''}>unknown</option>
            {customerTesting.map((user, i) => (
              <option key={i} value={user.custNumber}>
                {user.name}
              </option>
            ))}
          </select>
        </div>
        <div className={`${styles.devToolSelect}`}>
          Customer Number
          <input
            name="customer-number"
            disabled={false}
            value={appContext.customerNumber}
            onChange={(event) => handleChangeCustomerNumber(event.target.value)}
          />
        </div>
        <div className={`${styles.devToolSelect}`}>
          Payment Platform
          <select
            name="payMethod"
            value={devToolsStore.payPlatform}
            onChange={(event) => handleChangePaymentPlatform(event.target.value)}
          >
            <option value={''}>No override</option>
            <option value={PaymentPlatform.PayAtExternal}>Pay@ external</option>
            <option value={PaymentPlatform.PayAtRedirect}>Pay@ redirect</option>
            <option value={PaymentPlatform.DPP}>DPP redirect</option>
            <option value={PaymentPlatform.RapydRedirect}>Rapyd redirect</option>
            <option value={PaymentPlatform.RapydEmbedded}>Rapyd embedded</option>
            <option value={PaymentPlatform.PaymentRequestDirectLink}>Direct payment link</option>
            <option value={PaymentPlatform.PaymentRequestMomentCheckout}>Moment Checkout</option>
            <option value={PaymentPlatform.PaymentRequestMoment}>Moment Redirect</option>
          </select>
        </div>
        <div className={`${styles.devToolSelect}`}>
          Mock Data:
          <input
            type="checkbox"
            id={'mockData'}
            defaultChecked={localStorage.getItem('ci.mock-data-override') === 'true' || false}
            // value={item.name}
            onChange={(event) => handleMockDataChange(event.target.checked)}
            className={styles.devCheckbox}
            disabled
          />
        </div>
        {(appContext.isAuthorized && (
          <div className={styles.devToolButton}>
            <Button meta={{ type: ButtonType.OutlineLight, size: ButtonSize.Small }} onClick={handleLogout}>
              Logout
            </Button>
          </div>
        )) || (
            <div className={styles.devToolButton}>
              <Button meta={{ type: ButtonType.OutlineLight, size: ButtonSize.Small }} onClick={handleLogin}>
                Login
              </Button>
            </div>
          )}
        <div className={styles.devToolButton}>
          <Button meta={{ type: ButtonType.OutlineLight, size: ButtonSize.Small }} onClick={handleHide}>
            Hide
          </Button>
        </div>
        <div className={styles.devToolButton}>
          <Button meta={{ type: ButtonType.OutlineLight, size: ButtonSize.Small }} onClick={handleCSAT}>
            Trigger CSAT
          </Button>
        </div>
        <div className={styles.devToolButton}>
          <Button meta={{ type: ButtonType.OutlineLight, size: ButtonSize.Small }} onClick={clearSessionStorage}>
            Clear Session Storage
          </Button>
        </div>
        <div className={styles.devToolButton}>
          <Button meta={{ type: ButtonType.OutlineLight, size: ButtonSize.Small }} onClick={refreshToken}>
            Refresh Token
          </Button>
        </div>
      </div>
    </FeaturePanel>
  );
});
export default DevTools;